import React, { useState, useRef, useCallback } from 'react';
import {
  Modal,
  Upload,
  Button,
  Progress,
  Typography,
  Space,
  Alert,
  Descriptions,
  Tag,
  Divider,
  InputNumber,
  Switch,
} from 'antd';
import {
  UploadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import Papa from 'papaparse';
import { AppType, Application } from 'types/application.types';

const { Text } = Typography;
interface APAUserPayload {
  os: string;
  country: string;
  af_id: string;
  firebase_project_id: string;
  push_token: string;
  locale: string;
  bundle_id: string;
  installDate: string;
  dep?: string;
  reg?: string;
  offer?: string;
}

interface PWAUserPayload {
  userID: string;
  language: string;
  install_datatime: string;
  dep?: string;
  reg?: string;
  os: string;
  name: string;
  firebaseToken: string;
  offer?: string;
}

type UserPayload = APAUserPayload | PWAUserPayload;
const LOCALE_MAP: Record<string, string> = {
  'Немецкий': 'de', 'Французский': 'fr', 'Португальский': 'pt',
  'Испанский': 'es', 'Итальянский': 'it', 'Английский': 'en',
  'Русский': 'ru', 'Польский': 'pl', 'Турецкий': 'tr',
  'Датский': 'dk', 'Словацкий': 'sk', 'Венгерский': 'hu',
  'Чешский': 'cs', 'Словенский': 'sl', 'Голландский': 'nl',
  'Румынский': 'ro', 'Арабский': 'ar', 'Финский': 'fi',
  'Греческий': 'el', 'Украинский': 'uk', 'Шведский': 'sv',
  'Норвежский': 'no', 'Каталонский': 'ca', 'Болгарский': 'bg',
  'Албанский': 'sq', 'Китайский': 'zh', 'Узбекский': 'uz',
  'Персидский': 'fa', 'Грузинский': 'ka', 'Вьетнамский': 'vi',
  'Литовский': 'lt', 'Японский': 'ja', 'Малайский': 'ms',
  'Тайский': 'th', 'Латвийский': 'lv', 'Боснийский': 'bs',
  'Македонский': 'mk', 'Сербский': 'sr', 'Иврит': 'he',
};

const normalizeLocale = (v: string): string => LOCALE_MAP[v] ?? (v.length > 10 ? v.slice(0, 10) : v);

const parseInstallDate = (raw: string): string | null => {
  const m1 = raw.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/);
  if (m1) return new Date(`${m1[1]}T${m1[2]}Z`).toISOString();
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString();
  return null;
};

const APA_REQUIRED = ['os', 'af_id', 'firebase_project_id', 'push_token', 'locale', 'bundle_id', 'installDate'];
const PWA_REQUIRED = ['userID', 'os', 'name', 'firebaseToken', 'install_datatime', 'language'];
interface Props {
  open: boolean;
  onClose: () => void;
  application: Application;
  baseURL: string;
  onImportDone: () => void;
}

interface ImportStats {
  total: number;
  sent: number;
  ok: number;
  fail: number;
  skippedDup: number;
  skippedDate: number;
  errors: string[];
}

const INITIAL_STATS: ImportStats = {
  total: 0, sent: 0, ok: 0, fail: 0, skippedDup: 0, skippedDate: 0, errors: [],
};

const ImportUsersModal: React.FC<Props> = ({ open, onClose, application, baseURL, onImportDone }) => {
  const isPWA = application.type === AppType.PWA;
  const endpoint = isPWA ? '/api/pwauser/add-user' : '/api/publicapa/add-user';
  const requiredCols = isPWA ? PWA_REQUIRED : APA_REQUIRED;

  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [stats, setStats] = useState<ImportStats>(INITIAL_STATS);
  const [importing, setImporting] = useState(false);
  const [rateLimit, setRateLimit] = useState(100);
  const [dedup, setDedup] = useState(true);

  const abortRef = useRef(false);

  const handleFile = (file: File) => {
    setValidationError(null);
    setStats(INITIAL_STATS);
    setRows([]);
    setHeaders([]);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const h = result.meta.fields ?? [];
        setHeaders(h);

        const missing = requiredCols.filter(c => !h.includes(c));
        if (missing.length) {
          setValidationError(`Отсутствуют обязательные колонки: ${missing.join(', ')}`);
          return;
        }
        setRows(result.data);
      },
      error: (err) => setValidationError(`Ошибка парсинга CSV: ${err.message}`),
    });

    return false;
  };

  const buildPayload = useCallback((row: Record<string, string>): UserPayload | null => {
    if (isPWA) {
      const installDate = parseInstallDate(row['install_datatime'] ?? '');
      if (!installDate) return null;
      return {
        userID: row['userID'],
        language: normalizeLocale(row['language']),
        install_datatime: installDate,
        os: row['os'],
        name: row['name'],
        firebaseToken: row['firebaseToken'],
        dep: row['dep'] || undefined,
        reg: row['reg'] || undefined,
        offer: row['offer'] || undefined,
      };
    } else {
      const installDate = parseInstallDate(row['installDate'] ?? '');
      if (!installDate) return null;
      return {
        os: row['os'],
        country: row['country'] ?? '',
        af_id: row['af_id'],
        firebase_project_id: row['firebase_project_id'],
        push_token: row['push_token'],
        locale: normalizeLocale(row['locale']),
        bundle_id: row['bundle_id'],
        installDate: installDate,
        dep: row['dep'] || undefined,
        reg: row['reg'] || undefined,
        offer: row['offer'] || undefined,
      };
    }
  }, [isPWA]);

  const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

  const startImport = async () => {
    abortRef.current = false;
    setImporting(true);
    setStats(INITIAL_STATS);

    const seenTokens = new Set<string>();
    const seenIds = new Set<string>();
    const delayMs = 1000 / rateLimit;

    let s: ImportStats = { ...INITIAL_STATS, total: rows.length };

    for (let i = 0; i < rows.length; i++) {
      if (abortRef.current) break;
      const row = rows[i];
      if (dedup) {
        const tokenKey = isPWA ? row['firebaseToken'] : row['push_token'];
        const idKey = isPWA ? row['userID'] : row['af_id'];
        if (seenTokens.has(tokenKey) || seenIds.has(idKey)) {
          s = { ...s, skippedDup: s.skippedDup + 1 };
          setStats({ ...s });
          continue;
        }
        seenTokens.add(tokenKey);
        seenIds.add(idKey);
      }

      const payload = buildPayload(row);
      if (!payload) {
        s = { ...s, skippedDate: s.skippedDate + 1 };
        setStats({ ...s });
        continue;
      }

      try {
        const resp = await fetch(`${baseURL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (resp.ok) {
          s = { ...s, sent: s.sent + 1, ok: s.ok + 1 };
        } else {
          const id = isPWA ? (row['userID'] ?? '') : (row['af_id'] ?? '');
          const errText = await resp.text().catch(() => '');
          s = {
            ...s,
            sent: s.sent + 1,
            fail: s.fail + 1,
            errors: s.errors.length < 50
              ? [...s.errors, `${id}: HTTP ${resp.status} ${errText.slice(0, 80)}`]
              : s.errors,
          };
        }
      } catch (err) {
        const id = isPWA ? (row['userID'] ?? '') : (row['af_id'] ?? '');
        s = {
          ...s,
          sent: s.sent + 1,
          fail: s.fail + 1,
          errors: s.errors.length < 50
            ? [...s.errors, `${id}: ${err instanceof Error ? err.message : 'network error'}`]
            : s.errors,
        };
      }

      setStats({ ...s });
      await sleep(delayMs);
    }

    setImporting(false);
    if (!abortRef.current) onImportDone();
  };

  const stopImport = () => { abortRef.current = true; };

  const processed = stats.sent + stats.skippedDup + stats.skippedDate;
  const pct = stats.total > 0 ? Math.round((processed / stats.total) * 100) : 0;

  return (
    <Modal
      title={`Импорт пользователей (${isPWA ? 'PWA' : 'Android/iOS'})`}
      open={open}
      onCancel={() => { stopImport(); onClose(); }}
      footer={null}
      width={640}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Приложение">{application.name}</Descriptions.Item>
          <Descriptions.Item label="Тип">
            <Tag color={isPWA ? 'blue' : 'green'}>{isPWA ? 'PWA' : 'Android/iOS'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Endpoint">
            <code>{endpoint}</code>
          </Descriptions.Item>
          <Descriptions.Item label="Обязательные колонки">
            {requiredCols.map(c => <Tag key={c} style={{ marginBottom: 2 }}>{c}</Tag>)}
          </Descriptions.Item>
        </Descriptions>
        <Upload beforeUpload={handleFile} accept=".csv" showUploadList={false} maxCount={1}>
          <Button icon={<UploadOutlined />} disabled={importing}>Выбрать CSV файл</Button>
        </Upload>

        {validationError && <Alert type="error" message={validationError} showIcon />}

        {rows.length > 0 && !validationError && (
          <>
            <Alert
              type="info"
              message={`Загружено ${rows.length} строк, колонки: ${headers.length}`}
              showIcon
            />
            <Space>
              <Text>Запросов/сек:</Text>
              <InputNumber
                min={1}
                max={500}
                value={rateLimit}
                onChange={v => v && setRateLimit(v)}
                disabled={importing}
                size="small"
                style={{ width: 80 }}
              />
              <Divider type="vertical" />
              <Text>Дедупликация:</Text>
              <Switch checked={dedup} onChange={setDedup} disabled={importing} size="small" />
            </Space>
            <Space>
              {!importing ? (
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={startImport}
                  disabled={rows.length === 0}
                >
                  Начать импорт
                </Button>
              ) : (
                <Button danger icon={<PauseCircleOutlined />} onClick={stopImport}>
                  Остановить
                </Button>
              )}
            </Space>
            {(importing || stats.sent > 0) && (
              <>
                <Progress percent={pct} status={importing ? 'active' : stats.fail > 0 ? 'exception' : 'success'} />
                <Space size="large" wrap>
                  <Text>Отправлено: <strong>{stats.sent}</strong></Text>
                  <Text style={{ color: '#52c41a' }}>
                    <CheckCircleOutlined /> OK: {stats.ok}
                  </Text>
                  <Text style={{ color: '#ff4d4f' }}>
                    <CloseCircleOutlined /> Ошибки: {stats.fail}
                  </Text>
                  <Text type="secondary">Дупликаты: {stats.skippedDup}</Text>
                  <Text type="secondary">Плохая дата: {stats.skippedDate}</Text>
                </Space>

                {stats.errors.length > 0 && (
                  <div style={{ maxHeight: 150, overflow: 'auto', fontSize: 12, fontFamily: 'monospace', background: '#fafafa', padding: 8, borderRadius: 4 }}>
                    {stats.errors.map((e, i) => <div key={i} style={{ color: '#ff4d4f' }}>{e}</div>)}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Space>
    </Modal>
  );
};

export default ImportUsersModal;